import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TrendingUp, Shield, Zap, Globe, ArrowRight, ChevronRight, Download } from 'lucide-react';

export default function LandingPage() {
  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Advanced Trading Tools",
      description: "Real-time charts, technical indicators, and professional analysis tools at your fingertips."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure & Regulated",
      description: "Your funds are protected with bank-level security and regulatory compliance."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast Execution",
      description: "Execute trades in milliseconds with our high-performance trading infrastructure."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Markets",
      description: "Access 50+ currency pairs and trade 24/5 across all major forex markets."
    }
  ];

  const pairs = [
    { pair: "EUR/USD", price: "1.0852", change: "+0.15%" },
    { pair: "GBP/USD", price: "1.2648", change: "-0.08%" },
    { pair: "USD/JPY", price: "149.52", change: "+0.22%" },
    { pair: "AUD/USD", price: "0.6553", change: "+0.11%" },
  ];

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white font-['Outfit']">GeneiProFx</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors">Features</a>
              <a href="#markets" className="text-zinc-400 hover:text-white transition-colors">Markets</a>
              <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">Pricing</a>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/download">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
                  <Download className="w-4 h-4 mr-2" />
                  App
                </Button>
              </Link>
              <Link to="/register">
                <Button className="glow-primary" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <span className="w-2 h-2 rounded-full bg-green-400 pulse-dot" />
                <span className="text-sm text-primary">Live Trading Available</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight leading-tight">
                Trade Forex Like a
                <span className="text-primary block">Professional</span>
              </h1>
              
              <p className="text-lg text-zinc-400 max-w-xl">
                Access global currency markets with advanced trading tools, real-time analytics, 
                and institutional-grade execution. Start with a free demo account.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="glow-primary text-base px-8" data-testid="hero-cta-btn">
                    Start Trading Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="text-base border-zinc-700 hover:bg-zinc-800" data-testid="hero-login-btn">
                    Sign In to Account
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-2xl font-bold text-white font-mono">$2.5T+</div>
                  <div className="text-sm text-zinc-500">Daily Volume</div>
                </div>
                <div className="w-px h-12 bg-zinc-800" />
                <div>
                  <div className="text-2xl font-bold text-white font-mono">50+</div>
                  <div className="text-sm text-zinc-500">Currency Pairs</div>
                </div>
                <div className="w-px h-12 bg-zinc-800" />
                <div>
                  <div className="text-2xl font-bold text-white font-mono">24/5</div>
                  <div className="text-sm text-zinc-500">Market Access</div>
                </div>
              </div>
            </div>
            
            {/* Live Prices Card */}
            <div className="glass-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Live Prices</h3>
                <span className="flex items-center gap-2 text-sm text-green-400">
                  <span className="w-2 h-2 rounded-full bg-green-400 pulse-dot" />
                  Live
                </span>
              </div>
              <div className="space-y-3">
                {pairs.map((item) => (
                  <div key={item.pair} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors">
                    <span className="font-medium text-white">{item.pair}</span>
                    <div className="text-right">
                      <div className="font-mono text-white">{item.price}</div>
                      <div className={`text-sm font-mono ${item.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                        {item.change}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/register" className="block">
                <Button className="w-full" variant="outline" data-testid="prices-cta-btn">
                  View All Markets
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-4">
              Everything You Need to Trade
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Professional-grade tools and features designed for both beginners and experienced traders.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="glass-card p-6 card-hover fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-6">
            Ready to Start Trading?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            Join thousands of traders who trust GeneiProFx for their forex trading needs. 
            Start with a free demo account and practice with virtual funds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="glow-primary text-base px-8" data-testid="cta-register-btn">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#09090B] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white font-['Outfit']">GeneiProFx</span>
            </div>
            <p className="text-zinc-500 text-sm">
              &copy; 2024 GeneiProFx. All rights reserved. Trading involves risk.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
